import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function RefundModal({
    onClose,
    memberData,
    refundSuccessMsg,
    paymentSuccessMsg,
    depositAmount,
    overdueCharges,
    damageCharges,
    finalRefund,
    refunding,
    onProcessRefund,
    paymentAmount,
    onChangePaymentAmount,
    paymentMethod,
    onChangePaymentMethod,
    paymentCollecting,
    onCollectPayment,
}) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900">
                        Refund & Dues Settlement
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5">
                    {/* Member summary */}
                    <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center text-xs">
                        <div>
                            <div className="font-semibold text-gray-900">{memberData.name}</div>
                            <div className="text-gray-500">ID: {memberData.clientId}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-gray-500">Plan</div>
                            <div className="font-semibold text-gray-900">{memberData.plan}</div>
                        </div>
                    </div>

                    {/* Success messages */}
                    {refundSuccessMsg && (
                        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm font-medium flex items-center gap-2">
                            <span className="text-lg">✓</span> {refundSuccessMsg}
                        </div>
                    )}

                    {paymentSuccessMsg && (
                        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm font-medium flex items-center gap-2">
                            <span className="text-lg">✓</span> {paymentSuccessMsg}
                        </div>
                    )}

                    {!refundSuccessMsg && !paymentSuccessMsg && (
                        <>
                            {/* Calculations */}
                            <div className="space-y-3">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Refund Calculation
                                </h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Deposit Amount</span>
                                        <span className="font-medium text-gray-900">₹{depositAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>Overdue Charges</span>
                                        <span className="font-medium text-red-600">- ₹{overdueCharges.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>Pending Damage Charges</span>
                                        <span className="font-medium text-red-600">- ₹{damageCharges.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-gray-100 pt-2 flex justify-between font-bold text-base">
                                        <span className={finalRefund >= 0 ? "text-gray-900" : "text-red-600"}>
                                            {finalRefund >= 0 ? "Final Refund" : "Outstanding Dues"}
                                        </span>
                                        <span className={finalRefund >= 0 ? "text-green-600" : "text-red-600"}>
                                            ₹{finalRefund.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Condition-based workflow */}
                            {finalRefund >= 0 ? (
                                <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-xs text-green-800 leading-relaxed">
                                    The member is eligible for a refund. Clicking "Issue Refund" will process the return of their deposit amount after deductions.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Warning UI */}
                                    <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-xs text-red-800 leading-relaxed">
                                        <div className="font-bold mb-1 flex items-center gap-1.5">
                                            <span className="text-base leading-none">⚠️</span>
                                            Outstanding Balance Warning
                                        </div>
                                        This member has a negative refund balance of ₹{Math.abs(finalRefund).toFixed(2)}. Refund cannot be requested until these dues are settled. Please collect payment below.
                                    </div>

                                    {/* Payment collection flow */}
                                    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/50">
                                        <h5 className="text-xs font-bold text-gray-700">
                                            Payment Collection Flow
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">
                                                    Amount to Collect
                                                </label>
                                                <input
                                                    type="number"
                                                    value={paymentAmount}
                                                    onChange={(e) => onChangePaymentAmount(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-black"
                                                    placeholder="Amount"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">
                                                    Payment Method
                                                </label>
                                                <select
                                                    value={paymentMethod}
                                                    onChange={(e) => onChangePaymentMethod(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-black"
                                                >
                                                    <option value="Cash">Cash</option>
                                                    <option value="UPI">UPI</option>
                                                    <option value="Card">Card</option>
                                                    <option value="Net Banking">Net Banking</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
                    >
                        {refundSuccessMsg || paymentSuccessMsg ? "Close" : "Cancel"}
                    </button>

                    {!refundSuccessMsg && !paymentSuccessMsg && (
                        <>
                            {finalRefund >= 0 ? (
                                <button
                                    onClick={onProcessRefund}
                                    disabled={refunding}
                                    className="px-4 py-2 bg-black text-white hover:bg-gray-900 rounded-lg text-sm font-semibold transition active:scale-95 disabled:bg-gray-400 flex items-center justify-center min-w-[120px]"
                                >
                                    {refunding ? "Processing..." : "Issue Refund"}
                                </button>
                            ) : (
                                <button
                                    onClick={onCollectPayment}
                                    disabled={paymentCollecting || !paymentAmount}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition active:scale-95 disabled:bg-gray-400 flex items-center justify-center min-w-[150px]"
                                >
                                    {paymentCollecting ? "Collecting..." : "Collect & Settle"}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
